import type {
  AdminHealth,
  HealthApp,
  HealthAppStatus,
  HealthDeployOutcome,
  HealthEnvironment,
  HealthFailure,
  HealthPoint,
  HealthRange,
} from '@traceio-org/platform-contracts';
import { HEALTH_RANGES } from '@traceio-org/platform-contracts';
import type { RecordStatusTone } from '@traceio-org/ui';
import { formatCount } from './format';

/**
 * Words and numbers of the Health page. Every number shown is one DigitalOcean returned (platform-api passes it on
 * unchanged); a null is shown as "No data", never as zero.
 */

export const ENVIRONMENT_NAMES: Readonly<Record<HealthEnvironment, string>> = {
  production: 'Production',
  dev: 'Dev',
};

export const RANGE_NAMES: Readonly<Record<HealthRange, string>> = {
  '24h': 'Last 24 hours',
  '7d': 'Last 7 days',
  '30d': 'Last 30 days',
};

export function rangeParam(value: string | null): HealthRange {
  return (HEALTH_RANGES as readonly string[]).includes(value ?? '') ? (value as HealthRange) : '24h';
}

/** The status pill: always the word with the dot, the tone only supplements it. */
export const APP_STATUS: Readonly<Record<HealthAppStatus, { label: string; tone: RecordStatusTone }>> = {
  healthy: { label: 'Healthy', tone: 'positive' },
  degraded: { label: 'Degraded', tone: 'warning' },
  down: { label: 'Down', tone: 'danger' },
  unknown: { label: 'Unknown', tone: 'neutral' },
};

/** Attention first (down, degraded, unknown), then by name, so the rows that matter lead the table. */
const STATUS_ORDER: Readonly<Record<HealthAppStatus, number>> = {
  down: 0,
  degraded: 1,
  unknown: 2,
  healthy: 3,
};

export function sortApps<A extends Pick<HealthApp, 'status' | 'name'>>(apps: readonly A[]): A[] {
  return [...apps].sort(
    (a, b) => STATUS_ORDER[a.status] - STATUS_ORDER[b.status] || a.name.localeCompare(b.name),
  );
}

export const DEPLOY_OUTCOMES: Readonly<Record<HealthDeployOutcome, string>> = {
  success: 'Deployed',
  failed: 'Failed',
  canceled: 'Canceled',
  in_progress: 'In progress',
};

const READ_NAMES: Readonly<Record<HealthFailure['read'], string>> = {
  project: 'The environment project',
  app: 'An app',
  app_health: 'Component health',
  deployments: 'Deployments',
  metrics: 'Metrics',
  uptime: 'Uptime checks',
  alerts: 'Alerts',
};

const METRIC_NAMES: Readonly<Record<string, string>> = {
  appCpu: 'app CPU',
  appMemory: 'app memory',
  appRequests: 'requests',
  appLatency: 'p95 latency',
  dbCpu: 'database CPU',
  dbMemory: 'database memory',
  dbDisk: 'database disk',
  pgConnections: 'Postgres connections',
  redisClients: 'Valkey clients',
};

/** One failed DigitalOcean read in words: what is missing, of what, and why. */
export function describeFailure(failure: HealthFailure): string {
  const what = READ_NAMES[failure.read];
  const target = failure.target ? (METRIC_NAMES[failure.target] ?? failure.target) : null;
  return `${what}${target ? ` (${target})` : ''}: ${failure.message}`;
}

const decimal = (digits: number) =>
  new Intl.NumberFormat(undefined, { maximumFractionDigits: digits, minimumFractionDigits: 0 });
const oneDecimal = decimal(1);
const twoDecimals = decimal(2);

export function formatPercent(value: number | null, digits = 1): string | null {
  if (value === null) return null;
  return `${(digits === 1 ? oneDecimal : decimal(digits)).format(value)}%`;
}

export function formatMs(value: number | null): string | null {
  return value === null ? null : `${formatCount(Math.round(value))} ms`;
}

/**
 * A p95 as staff should read it: "Not enough traffic" where DigitalOcean's value was a histogram artifact of too few
 * requests, "over 10 s" on the top bucket (that bucket means this or slower), else the milliseconds.
 */
export function formatLatency(latency: {
  latencyP95Ms: number | null;
  latencyP95AtTopBucket: boolean;
  latencyP95Reason: 'not_enough_traffic' | null;
}): string | null {
  if (latency.latencyP95Reason === 'not_enough_traffic') return 'Not enough traffic';
  if (latency.latencyP95Ms === null) return null;
  if (latency.latencyP95AtTopBucket) return `over ${formatCount(Math.round(latency.latencyP95Ms / 1000))} s`;
  return formatMs(latency.latencyP95Ms);
}

export function formatRate(value: number | null): string | null {
  if (value === null) return null;
  if (value > 0 && value < 0.01) return 'under 0.01/s';
  return `${twoDecimals.format(value)}/s`;
}

/** How long ago, in short words (12 s ago, 4 min ago, 3 h ago, 2 d ago). */
export function formatAge(iso: string, now: number): string {
  const seconds = Math.max(0, Math.round((now - Date.parse(iso)) / 1000));
  if (seconds < 60) return `${seconds} s ago`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 48) return `${hours} h ago`;
  return `${Math.floor(hours / 24)} d ago`;
}

const hourFormat = new Intl.DateTimeFormat(undefined, {
  hour: '2-digit',
  minute: '2-digit',
  timeZone: 'UTC',
});
const dayFormat = new Intl.DateTimeFormat(undefined, { month: 'short', day: 'numeric', timeZone: 'UTC' });

/** The label of a chart step: the time of day for 24 hours, the day for longer periods (UTC, as Trace Admin shows). */
export function stepLabel(iso: string, range: HealthRange): string {
  const date = new Date(iso);
  return range === '24h' ? hourFormat.format(date) : dayFormat.format(date);
}

/** A chart series: a missing measurement is NaN, which the chart shows as "Not available", never as zero. */
export function chartValues(points: readonly HealthPoint[]): number[] {
  return points.map((point) => (point.value === null ? Number.NaN : point.value));
}

export function hasValues(points: readonly HealthPoint[]): boolean {
  return points.some((point) => point.value !== null);
}

/** The average and peak of a series, over the steps DigitalOcean measured. */
export function seriesSummary(points: readonly HealthPoint[]): { average: number; peak: number } | null {
  const values = points.map((p) => p.value).filter((v): v is number => v !== null);
  if (values.length === 0) return null;
  return { average: values.reduce((a, b) => a + b, 0) / values.length, peak: Math.max(...values) };
}

/** The apps that need someone: down or degraded (unknown apps are named with the failed reads instead). */
export function appsNeedingAttention(health: AdminHealth): HealthApp[] {
  return sortApps(health.apps.filter((app) => app.status === 'down' || app.status === 'degraded'));
}

/**
 * Deploys of today (UTC, as Trace Admin shows) across the apps, from each app's latest deploys (platform-api passes
 * the few newest per app, enough for a day of normal work).
 */
export function deploysToday(apps: readonly HealthApp[], now: number): { total: number; failed: number } {
  const midnight = new Date(now).toISOString().slice(0, 10);
  const today = apps
    .flatMap((app) => app.recentDeploys)
    .filter((d) => new Date(d.createdAt).toISOString().slice(0, 10) === midnight);
  return { total: today.length, failed: today.filter((d) => d.outcome === 'failed').length };
}
